import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BellIcon } from '../components/icons'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { listNotifications, markAllNotificationsRead, type AppNotification } from '../lib/notifications'
import { NotificationList } from './NotificationList'

/**
 * Bildirim zili - kenar cubugu/header'da.
 *
 * ## Teslim: mount + odaklaninca, SSE/polling YOK
 *
 * Tek surec, tek SQLite yazari (bkz. `runs/broker.py` ayni gerekce). Bir
 * rozet sayisi icin ikinci bir SSE kanali acmak orantisiz - odak-yenileme
 * gercekci durumu (kullanici uzun bir uretimden sonra sekmeye donuyor)
 * zaten kapsiyor.
 */
export function NotificationBell() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<AppNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const container = useRef<HTMLDivElement>(null)
  const wrapper = useRef<HTMLSpanElement>(null)

  const load = useCallback(() => {
    listNotifications()
      .then((data) => {
        setItems(data.items)
        setUnreadCount(data.unreadCount)
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    load()
    const onFocus = () => load()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [load])

  useFocusTrap(container, { active: open, onClose: () => setOpen(false) })

  // Bos alana tiklayinca kapat - tetikleyici dugme HARIC (aksi halde tikla
  // -> kapat -> ayni tiklama "disari" sayilip aninda tekrar tetikler gibi
  // davranmaz ama yine de aciyor-kapatiyor dongusune girmemesi icin butonu
  // `wrapper` disina koymuyoruz, ikisini de kapsiyor).
  useEffect(() => {
    if (!open) return
    function close(event: MouseEvent) {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  async function openAndMarkRead() {
    const next = !open
    setOpen(next)
    if (next && unreadCount > 0) {
      await markAllNotificationsRead().catch(() => undefined)
      load()
    }
  }

  return (
    <span className="notification-bell" ref={wrapper}>
      <button
        type="button"
        className="btn-quiet notification-bell-trigger"
        onClick={() => void openAndMarkRead()}
        aria-expanded={open}
        aria-label={t('notifications.label')}
      >
        <BellIcon />
        {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
      </button>

      {open && (
        <div className="notification-popover" ref={container} tabIndex={-1} role="dialog" aria-label={t('notifications.label')}>
          <NotificationList items={items} onChanged={load} />
        </div>
      )}
    </span>
  )
}
